import { trpc } from '@/main'
import { useSuspenseQuery, type QueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useDeferredValue } from 'react'
import { deriveProfiles, useRootSearch } from './use-root-search'
import type { RootSearch } from '@/routes/__root'

/** The selected team as returned by `teams.getTeam` (incl. the viewer-scoped flags). */
export type Team = ReturnType<typeof useTeam>['team']

/**
 * SINGLE SOURCE of the `getTeam` selected-profile + filters derivation. This used to be written in
 * three places (the loader prefetch, the hooks) and drifting between them silently double-fetched.
 * Now the loader and `useTeam` derive it here, so the query key can never diverge — this is
 * the route query co-location convention (see AGENTS.md). Callers assemble the final input as
 * `{ teamId, filters }`.
 *
 * NOTE: `search` must ALREADY carry the derived `first`/`second` profiles. The hooks get them from
 * `useRootSearch`; a loader must merge `deriveProfiles(...)` in first (see `prefetchTeam`).
 */
export function teamGetTeamFilters(
  search: Pick<RootSearch, 'first' | 'second' | 'type' | 'o' | 'od'>,
  teamId: number | undefined,
) {
  const teamProfile =
    teamId != null && search.first?.type === 'team' && search.first.id === teamId
      ? search.first
      : teamId != null && search.second?.type === 'team' && search.second.id === teamId
        ? search.second
        : undefined

  const filters = {
    type: search.type,
    evaluation: teamProfile?.evaluation,
    members: teamProfile && 'members' in teamProfile ? teamProfile.members : undefined,
    sort: search.o,
    sortDirection: search.od,
  }

  return { teamProfile, filters }
}

/**
 * SINGLE SOURCE of the `searchMembersInfinite` query options (first page + `getNextPageParam`),
 * referenced by the members panel — so the streamed
 * key lines up with the panel's `useSuspenseInfiniteQuery` and the panel never refetches on reload.
 */
export function teamMembersInfiniteQueryOptions(
  args: Pick<RootSearch, 'q' | 'o' | 'od' | 'type'> & { teamId: number },
) {
  return trpc.teams.searchMembersInfinite.infiniteQueryOptions(
    {
      teamId: args.teamId,
      q: args.q,
      o: args.o,
      od: args.od,
      type: args.type,
    },
    { getNextPageParam: (lastPage) => lastPage.nextCursor },
  )
}

/**
 * Loader-side counterpart of `useTeam`: prefetches `getTeam` with the EXACT same input the hook
 * computes (via the shared `teamGetTeamInput`) so the streamed/prefetched key lines up — a mismatch
 * would double-fetch. The loader has only raw search, so it must derive `first`/`second` first.
 * Fire-and-forget (deferred streaming), mirroring the report-family loaders.
 */
export type TeamChromeSearchDeps = Pick<RootSearch, 'first' | 'second' | 'type' | 'o' | 'od' | 'q'>

/**
 * `loaderDeps` picker for the `teams.$teamId` layout: EXACTLY the search keys `prefetchTeamChrome`
 * consumes (getTeam filters + the members list), so the router's deps memoization re-runs the chrome
 * loader only when those can change — not on unrelated search churn (dialog-open ids, list sorts).
 */
export function teamChromeSearchDeps(search: TeamChromeSearchDeps): TeamChromeSearchDeps {
  return { first: search.first, second: search.second, type: search.type, o: search.o, od: search.od, q: search.q }
}

function prefetchTeam(queryClient: QueryClient, search: TeamChromeSearchDeps, params: { teamId: string }) {
  const teamId = Number(params.teamId)
  // The team subviews are never the /report page, so isReportPage is false here.
  const derivedSearch = { ...search, ...deriveProfiles(search, params, false) }
  const { filters } = teamGetTeamFilters(derivedSearch, teamId)
  // Return the promise so the layout loader can AWAIT it on the server (complete SSR HTML).
  return queryClient.prefetchQuery(trpc.teams.getTeam.queryOptions({ teamId, filters }))
}

/**
 * Loader-side prefetch of the PERSISTENT team chrome (TeamsLayout + TeamsMembersPanel): `getTeam` and the
 * members-infinite list. This lives in the `teams.$teamId` layout loader — that route renders real
 * content (the animated tab Outlet), so per AGENTS.md it owns the teamId-keyed chrome that all five tab
 * children read (the leaves no longer duplicate it). All deferred: the chrome reads
 * `getTeam`/`searchMembersInfinite` via suspense.
 */
export function prefetchTeamChrome(queryClient: QueryClient, search: TeamChromeSearchDeps, params: { teamId: string }) {
  prefetchTeam(queryClient, search, params)
  queryClient.prefetchInfiniteQuery(
    teamMembersInfiniteQueryOptions({
      teamId: Number(params.teamId),
      q: search.q,
      o: search.o,
      od: search.od,
      type: search.type,
    }),
  )
}

/**
 * The ONE way to read the selected team. Reads `getTeam` via `useSuspenseQuery` so the loading
 * skeleton is owned by the nearest route `pendingComponent` / `<Suspense>` boundary and the SSR
 * result streams in with no hydration mismatch — there is deliberately NO non-suspense variant, so a
 * caller can never serialize a pending `getTeam` on the server and hydrate it resolved on the client.
 * The input is memoized + deferred so a filter/type change keeps the PREVIOUS team on screen while the
 * new data loads (keep-previous UX, like the report). Uses the shared `teamGetTeamFilters` so the key
 * stays identical to `prefetchTeam` — no double fetch.
 *
 * `teamIdFromProp` overrides the route param (the evaluation selector reading a search-profile's team —
 * that id derives from `search.first`/`second`, so `prefetchReport` prefetches the same key). The caller
 * MUST guarantee a teamId exists (this is the suspense contract — there is no skipToken path); for
 * genuinely-OPTIONAL reads (the entity may be absent) use a `skipToken` `useQuery` at the call site
 * instead, since suspense can't express "fetch only when present" — see AGENTS.md.
 */
export function useTeam(teamIdFromProp?: number) {
  const search = useRootSearch()
  const { teamId: teamIdParam } = useParams({ strict: false })
  const teamId = teamIdFromProp ?? Number(teamIdParam)

  const { teamProfile, filters } = teamGetTeamFilters(search, teamId)
  const input = { teamId, filters }
  const deferredInput = useDeferredValue(input)
  // prefetch: descendant — from the teams LIST route this only mounts under a matched $teamId child (whose chrome loader prefetches it); report-family routes prefetch the profile-derived team in prefetchReport
  const { data: team, isFetching } = useSuspenseQuery(trpc.teams.getTeam.queryOptions(deferredInput))

  return { team, isFetching, teamProfile }
}
